// Website catalog client. The website backend forwards these calls (signed) to the Contextral dashboard, which owns
// the ready-made applications, matching, the assistant and bookings. The catalog never shows prices or plans:
// those are covered on the call. A catalog intake is booked through the scheduler (availability and bookings).
import type { ExistingBooking, Intake, IntakeDetails } from "./schedulerApi";

const API_BASE = import.meta.env.VITE_API_BASE || "";
const CATALOG = `${API_BASE}/api/catalog`;

export type PickId = "workspace" | "data" | "payment";
export type Picks = Partial<Record<PickId, string>>;

export interface Pick {
  id: PickId;
  question: string;
  options: { id: string; label: string }[];
}

/** One tool the visitor may already use: a non-internal library connector. `logo` is the brand mark, or null. */
export interface CatalogTool {
  name: string;
  logo: string | null;
}

/** A service sold alongside a build (library `offerings/`), as a plain-language card. Never a price or plan. */
export interface CatalogOffering {
  id: string;
  headline: string;
  summary: string;
}

export interface CatalogSchema {
  picks: Pick[];
  tools: CatalogTool[];
  offerings: CatalogOffering[];
  business_models: { id: string; label: string }[];
  industries: string[];
  problems: string[];
  team_sizes: string[];
  commit_sha: string;
}

/** The setup that suits the visitor. `solution` is kept for later calls and is never displayed. */
export interface Setup {
  solution: string;
  workspace: string;
  explanation: string;
}

export interface Listing {
  id: string;
  name: string;
  headline: string;
  tagline?: string | null;
  summary: string;
  audience?: string[];
  industries?: string[];
  business_model?: string;
  highlights: { title: string; description: string }[];
  not_included?: string[];
  call_to_action?: string | null;
  featured?: boolean;
  time_saved: string;
  works_with: string[];
  you_provide: string[];
  problems: string[];
  where_it_runs: string;
  /** The visitor's tools it will be set up to work with. */
  set_up_for: string[];
  /** Ids of the offerings (services) set up alongside this application; names come from `schema.offerings`. */
  needs?: string[];
  commit_sha: string;
  temporary?: boolean;
}

export interface Answers {
  picks: Picks;
  tools: string[];
  /** Tools the visitor typed in that the picker doesn't list — a signal for connectors we could build. */
  other_tools: string[];
  business_model?: string;
  industries: string[];
  problems: string[];
  team_size?: string;
  volumes: string;
  /** More about what they need, in their own words, when the catalog asked a follow-up question. */
  details?: string;
}

export interface Scored {
  id: string;
  reasons: string[];
}

/** An application the catalog doesn't list, offered when nothing else fit. */
export interface CatalogSuggestion {
  id: string;
  name: string;
  summary: string;
}

export interface MatchResult {
  matches: Scored[];
  upsells: Scored[];
  suggestions: CatalogSuggestion[];
  /** Services (offerings) that fit what the visitor said; cards come from `schema.offerings`. */
  offerings: Scored[];
  matched: boolean;
  commit_sha: string;
}

export type ComposeResult =
  | { buildable: true; listing: Listing; token: string }
  /** `needs_details`: the catalog could put something together with one more answer to `question`. */
  | { buildable: false; reason: string; needs_details?: boolean; question?: string };

export interface Turn {
  role: "visitor" | "assistant";
  text: string;
}

export type Topic = "workspace" | "data" | "payment" | "tools" | "business" | "size" | "problems";

export interface AssistantReply {
  fallback: boolean;
  reply: string;
  answers: Answers;
  next_topic: Topic | null;
  summary: string | null;
  suggested_listing_ids: string[];
}

/** One selection: a ready-made listing, a custom selection with the token that proves it, or a service. */
export type CartItem = { listing_id: string } | { token: string } | { offering_id: string };

export interface CatalogIntake extends IntakeDetails {
  picks: Picks;
  answers: Answers;
  cart: CartItem[];
  assessment_summary: string;
  transcript: Turn[];
}

export class CatalogError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly fields: string[] = [],
    readonly existing: ExistingBooking | null = null,
  ) {
    super(message);
  }
}

const FALLBACK_MESSAGE = "Something went wrong. Please try again or email hello@contextral.com.";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${CATALOG}${path}`, { ...init, headers: { "Content-Type": "application/json" } });
  } catch {
    throw new CatalogError("We can't reach the catalog right now. Check your connection and try again.", "NETWORK", 0);
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = body?.error ?? {};
    throw new CatalogError(
      error.message || FALLBACK_MESSAGE,
      error.code || "CATALOG_ERROR",
      response.status,
      Array.isArray(error.details?.fields) ? error.details.fields : [],
      error.details?.existing_booking ?? null,
    );
  }
  return body as T;
}

const post = <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) });
const setupQuery = (setup: Setup) =>
  new URLSearchParams({ solution: setup.solution, workspace: setup.workspace }).toString();

export const emptyAnswers = (): Answers => ({ picks: {}, tools: [], other_tools: [], industries: [], problems: [], volumes: "" });

/** The dashboard may list tools as plain names; the UI always works with ``{name, logo}``. */
export const asTool = (item: CatalogTool | string): CatalogTool =>
  typeof item === "string" ? { name: item, logo: null } : { name: item.name, logo: item.logo ?? null };

export const catalogApi = {
  schema: async (): Promise<CatalogSchema> => {
    const body = await request<Omit<CatalogSchema, "tools" | "offerings"> & {
      tools?: (CatalogTool | string)[] | null;
      offerings?: Partial<CatalogOffering>[] | null;
    }>("/schema/");
    return {
      ...body,
      tools: (body.tools ?? []).map(asTool),
      offerings: (body.offerings ?? []).filter(
        (o): o is CatalogOffering => typeof o?.id === "string" && typeof o?.headline === "string" && !!o.headline,
      ),
    };
  },
  resolve: (picks: Picks) => post<Setup>("/resolve/", { picks }),
  listings: (setup: Setup) => request<{ listings: Listing[]; commit_sha: string }>(`/listings/?${setupQuery(setup)}`),
  match: async (setup: Setup, answers: Answers, cart: string[]): Promise<MatchResult> => {
    const body = await post<Omit<MatchResult, "suggestions" | "offerings"> & {
      suggestions?: CatalogSuggestion[] | null; offerings?: Scored[] | null;
    }>(`/match/?${setupQuery(setup)}`, { answers, cart });
    return { ...body, suggestions: (body.suggestions ?? []).filter((s) => s && s.name && s.summary),
             offerings: (body.offerings ?? []).filter((o) => o && typeof o.id === "string") };
  },
  /** `asked`: follow-up questions already answered for this attempt (the catalog asks at most two). */
  compose: (setup: Setup, answers: Answers, needs: string[], asked = 0) =>
    post<ComposeResult>(`/compose/?${setupQuery(setup)}`, { answers, needs, asked }),
  assistant: (body: { transcript: Turn[]; answers: Answers; skipped: Topic[]; message: string }) =>
    post<AssistantReply>("/assistant/", body),
  intake: (details: CatalogIntake) => post<Intake | { existing_booking: ExistingBooking }>("/intake/", details),
};
